import { ArrowRight, Flame } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { BRAND } from "../data/site";
import { GopuramMark } from "./brand/Icons";

export default function Hero() {
  const navigate = useNavigate();
  const handleScroll = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative overflow-hidden bg-cream grain pt-32 md:pt-40 pb-20 md:pb-28"
    >
      {/* decorative motifs */}
      <GopuramMark className="hidden md:block absolute -top-4 right-10 w-32 h-32 text-gold opacity-20" />
      <GopuramMark className="hidden md:block absolute bottom-6 left-6 w-24 h-24 text-gold opacity-15 rotate-[8deg]" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center">
        {/* Left: copy */}
        <div className="lg:col-span-7 reveal">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-gold font-semibold">
            <span className="inline-block h-px w-8 bg-gold" />
            Andhra Style · Authentic Taste
          </div>

          <h1 className="font-display mt-6 text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-cocoa">
            <span className="sr-only">
              Andhra Biryani Co — authentic Andhra &amp; Hyderabadi dum biryani.{" "}
            </span>
            Dum-cooked.
            <br />
            Spice-rich.{" "}
            <span className="font-script text-gold text-6xl md:text-7xl lg:text-8xl align-[-0.05em]">
              Andhra
            </span>{" "}
            born.
          </h1>

          <p className="mt-7 max-w-xl text-base md:text-lg text-cocoa-soft leading-relaxed">
            From Guntur chillies to Godavari grain — every handi of biryani at{" "}
            <span className="font-semibold text-cocoa">Andhra Biryani Co</span>{" "}
            is a tribute to the kitchens of coastal Andhra. No shortcuts. No
            stored gravy. Just fire, patience and family recipe.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate("/menu")}
              data-testid="hero-view-menu-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C8A14B] text-[#FAF3E7] hover:bg-[#B89240] hover:-translate-y-0.5 transition-all px-7 py-3.5 font-semibold shadow-md"
            >
              View the Menu
              <ArrowRight size={18} />
            </button>
            <button
              onClick={() => handleScroll("locations")}
              data-testid="hero-find-store-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#C8A14B] text-[#3A1B0E] hover:bg-[#C8A14B] hover:text-[#FAF3E7] transition-all px-7 py-3 font-semibold"
            >
              Find a Kitchen
            </button>
          </div>

          <div className="mt-12 flex items-center gap-8 text-cocoa-soft text-sm">
            <div className="flex items-center gap-2">
              <Flame size={16} className="text-chilli" />
              <span>Guntur Chilli, Single-Origin</span>
            </div>
            <div className="hidden sm:block h-6 w-px bg-[#E8D8C0]" />
            <div className="hidden sm:block">Dum-cooked in Clay Handi</div>
            <div className="hidden md:block h-6 w-px bg-[#E8D8C0]" />
            <div className="hidden md:block">Fresh — Daily, Never Stored</div>
          </div>
        </div>

        {/* Right: photo */}
        <div className="lg:col-span-5 relative reveal">
          <div className="absolute -inset-6 rounded-[2.2rem] bg-gradient-to-tr from-[#C8A14B]/30 via-transparent to-[#C0392B]/15 blur-2xl" aria-hidden="true" />
          <div className="relative rounded-[2rem] overflow-hidden bg-[#3A1B0E] ring-1 ring-[#C8A14B]/60 shadow-2xl">
            <img
              src={BRAND.heroPhoto}
              alt="Clay-pot Andhra biryani"
              className="w-full h-[460px] md:h-[560px] object-cover"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#3A1B0E]/35 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between text-[#FAF3E7]">
              <div>
                <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4A24E]">
                  Signature
                </div>
                <div className="font-display text-2xl mt-1">
                  Andhra Chicken Dum
                </div>
              </div>
              <div className="rounded-full bg-[#FAF3E7]/95 text-[#3A1B0E] text-xs font-bold px-3 py-1.5 shadow">
                ₹349
              </div>
            </div>
          </div>

          {/* floating chip */}
          <div className="absolute -top-5 -right-5 md:-right-8 bg-card-warm border border-cream-deep rounded-2xl px-5 py-3 shadow-lg hidden md:flex items-center gap-3">
            <div className="font-display text-3xl text-gold leading-none">★ 4.9</div>
            <div className="text-[11px] uppercase tracking-wider text-cocoa-soft leading-tight">
              From 2,400+
              <br /> happy guests
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
